import { useEffect, useState } from "react";
import { Eye, MousePointerClick, MessageCircle, Send, Loader2 } from "lucide-react";
import { http } from "@/lib/api";

const TILES = [
  {
    key: "sent",
    label: "Sent",
    icon: Send,
    tone: "text-ink-900",
    chip: "text-ink-400",
    chipBg: "bg-ink-50",
    getValue: (d) => d.totals.sent,
    getSub:   (d) => `last 30 days`,
  },
  {
    key: "opens",
    label: "Opens",
    icon: Eye,
    tone: "text-[#0D47A1]",
    chip: "text-[#0D47A1]",
    chipBg: "bg-[#E6EEF8]",
    getValue: (d) => d.totals.opens || 0,
    getSub:   (d) => `${d.rates.open_rate}% open rate`,
  },
  {
    key: "clicks",
    label: "Clicks",
    icon: MousePointerClick,
    tone: "text-[#FF6B35]",
    chip: "text-[#FF6B35]",
    chipBg: "bg-[#FFF1E8]",
    getValue: (d) => d.totals.clicks || 0,
    getSub:   (d) => `${d.rates.click_rate}% click rate`,
  },
  {
    key: "replies",
    label: "Replies",
    icon: MessageCircle,
    tone: "text-[#1B5E20]",
    chip: "text-[#1B5E20]",
    chipBg: "bg-[#E6F4EA]",
    getValue: (d) => d.totals.replies || 0,
    getSub:   (d) => `${d.rates.reply_rate}% reply rate`,
  },
];

/**
 * Combined Outreach performance card.
 * Each KPI tile shows the count as the primary value with the percentage rate
 * as a subtle sub-stat chip — so volume and engagement live side-by-side.
 */
export default function OutreachStats({ showEmpty = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    http
      .get("/outreach/stats")
      .then((r) => alive && setData(r.data))
      .catch((err) => console.warn("Failed to load outreach stats:", err))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  if (loading) {
    return (
      <div className="bg-white border border-ink-100 rounded-sm p-6 mb-8 text-sm text-ink-400">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading outreach stats…
      </div>
    );
  }

  if (!data || data.totals.sent === 0) {
    if (!showEmpty) return null;
    return (
      <div className="bg-white border border-dashed border-ink-200 rounded-sm p-10 text-center mb-8" data-testid="outreach-stats-empty">
        <Send className="w-6 h-6 mx-auto text-ink-300 mb-2" />
        <p className="text-ink-500 text-sm">
          No emails sent yet. Once you start sending — manually or via a sequence —
          your 30-day open, click, and reply performance will land here.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-ink-100 rounded-sm p-6 mb-8" data-testid="outreach-stats">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400">
            Outreach performance · last 30 days
          </div>
          <p className="text-[11px] text-ink-500 mt-1">
            Counts are headline; rates beneath each tile show engagement
            quality.{" "}
            <a
              href="#stats-legend"
              className="text-brand hover:underline"
              data-testid="stats-legend-anchor"
            >
              How are these tracked?
            </a>
          </p>
        </div>
        <div className="text-[11px] font-mono text-ink-400 text-right hidden md:block">
          {data.totals.unique_open}/{data.totals.sent} opened ·{" "}
          {data.totals.unique_click}/{data.totals.sent} clicked ·{" "}
          {data.totals.unique_reply}/{data.totals.sent} replied
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {TILES.map((t) => {
          const Icon = t.icon;
          return (
            <div
              key={t.key}
              data-testid={`kpi-${t.key}`}
              className="border border-ink-100 rounded-sm p-4 flex flex-col gap-2"
            >
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widestx font-mono text-ink-400">
                <Icon className="w-3 h-3" /> {t.label}
              </div>
              <div className={`font-display text-3xl font-extrabold leading-none ${t.tone}`}>
                {t.getValue(data)}
              </div>
              <div
                className={`inline-block self-start text-[10px] uppercase tracking-widestx font-semibold px-2 py-0.5 rounded-sm ${t.chip} ${t.chipBg}`}
                data-testid={`kpi-${t.key}-rate`}
              >
                {t.getSub(data)}
              </div>
            </div>
          );
        })}
      </div>

      <Sparkline series={data.series} />

      <div id="stats-legend" className="border-t border-ink-100 mt-6 pt-4 text-[11px] text-ink-600 leading-relaxed">
        <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
          How tracking works
        </div>
        <ul className="grid sm:grid-cols-3 gap-x-6 gap-y-1.5 list-disc pl-5">
          <li>
            <strong className="text-ink-900">Opens</strong> use a 1×1 pixel and back-fill from replies
            (benchmark 20–60%).
          </li>
          <li>
            <strong className="text-ink-900">Clicks</strong> are tracked on every <code className="px-1 bg-ink-100 rounded">https://</code> URL in the body
            (benchmark 2–8%).
          </li>
          <li>
            <strong className="text-ink-900">Replies</strong> are matched via IMAP polling on
            Message-ID / In-Reply-To (benchmark 1–10%).
          </li>
        </ul>
      </div>
    </div>
  );
}

function Sparkline({ series }) {
  if (!series?.length) return null;
  const W = 720;
  const H = 70;
  const max = Math.max(1, ...series.map((s) => s.sent));
  const stepX = W / Math.max(1, series.length - 1);
  const toY = (v) => H - 4 - (v / max) * (H - 12);

  const path = series
    .map((s, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(1)},${toY(s.sent).toFixed(1)}`)
    .join(" ");

  return (
    <div className="border-t border-ink-100 pt-4" data-testid="outreach-sparkline">
      <div className="text-[10px] uppercase tracking-widestx font-mono text-ink-400 mb-2">
        Daily volume
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="w-full h-[70px]"
      >
        <path d={path} fill="none" stroke="#0F1115" strokeWidth="1.5" />
        {series.map((s, i) =>
          s.replies ? (
            <circle key={`reply-${i}-${s.date}`} cx={i * stepX} cy={toY(s.sent)} r="2.5" fill="#1B5E20" />
          ) : null
        )}
      </svg>
      <div className="flex items-center justify-between text-[10px] font-mono text-ink-400 mt-1">
        <span>{series[0]?.date}</span>
        <span className="inline-flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-[#1B5E20] inline-block" /> day with reply
        </span>
        <span>{series[series.length - 1]?.date}</span>
      </div>
    </div>
  );
}
